import React from 'react';
import PropTypes from 'prop-types';
import CwDropdown from '../cwdropdown/CwDropdown';
import CwButton from '../cwbutton/CwButton';
import { setMenuPositions, calcDropdownPosition } from '../../utils/CwDropdownService';
import { getMouseMode } from '../../utils/CwThemeService';

class CwMenuButton extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: '',
      expanded: false,
      highlightedIndex: null,
    };
    this.keyDownHandlers = {
      ArrowDown(event) {
        event.preventDefault();
        const itemsLength = this.getFilteredItems().length;
        if (!itemsLength) return;
        let { highlightedIndex } = this.state;
        let index = (
          highlightedIndex === null ||
          highlightedIndex === itemsLength - 1
        ) ? 0 : highlightedIndex + 1;
        this._performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          expanded: true,
        });
      },
      ArrowUp(event) {
        event.preventDefault();
        const itemsLength = this.getFilteredItems().length;
        if (!itemsLength) return;
        let { highlightedIndex } = this.state;
        let index = (
          highlightedIndex === 0 ||
          highlightedIndex === null
        ) ? itemsLength - 1 : highlightedIndex - 1;
        this._performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          expanded: true,
        });
      },
      Enter(event) {
        if (this.state.expanded === false) {
          event.preventDefault();
          const itemsLength = this.getFilteredItems().length;
          if (!itemsLength) return;
          let { highlightedIndex } = this.state;
          let index = (
            highlightedIndex === null ||
            highlightedIndex === itemsLength - 1
          ) ? 0 : highlightedIndex + 1;
          this._performAutoCompleteOnKeyUp = true;
          this.setState({
            highlightedIndex: index,
            expanded: true,
          });
          // menu is closed so there is no selection to accept -> do nothing
        }
        else if (this.state.highlightedIndex == null) {
          // input has focus but no menu item is selected + enter is hit -> close the menu, highlight whatever's in input
          this.setState({
            expanded: false
          });
        }
        else {
          // text entered + menu item has been highlighted + enter is hit -> update value to that of selected menu item, close the menu
          event.preventDefault();
          let item = this.getFilteredItems()[this.state.highlightedIndex];
          this.onMenuItemSelected(item);
        }
      },
      Escape() {
        this.setState({
          highlightedIndex: null,
          expanded: false
        });
      }
    };
  }

  onMenuItemSelected(item) {
    this.setState({
      value: item.name,
      expanded: false,
      highlightedIndex: null
    }, () => {
      if (this.props.onChange) {
        this.props.onChange(item);
      }

      if (this.props.onDropDownItemClick) {
        this.props.onDropDownItemClick(item);
      }
    });
  };

  componentWillMount() {
    this._ignoreBlur = false;
    this._performAutoCompleteOnUpdate = false;
    this._performAutoCompleteOnKeyUp = false;
    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  componentWillReceiveProps(nextProps) {
    this._performAutoCompleteOnUpdate = true;
    // If `items` has changed we want to reset `highlightedIndex`
    // since it probably no longer refers to a relevant item
    if (this.props.items !== nextProps.items ||
      // The entries in `items` may have been changed even though the
      // object reference remains the same, double check by seeing
      // if `highlightedIndex` points to an existing item
      this.state.highlightedIndex >= nextProps.items.length) {
      this.setState({ highlightedIndex: null });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.expanded === true && prevState.expanded === false) {
      const menuPositions = setMenuPositions(this.cwButton.cwButton);
      this.setState({
        menuTop: menuPositions.menuTop,
        menuLeft: menuPositions.menuLeft,
        menuWidth: menuPositions.menuWidth,
        inputHeight: menuPositions.inputHeight
      });
      if (this.props.onExpand) {
        this.props.onExpand(this.state.expanded);
      }
    }
  }

  setIgnoreBlur = (ignore) => {
    this._ignoreBlur = ignore;
  };

  getFilteredItems() {
    return this.props.items;
  }

  handleScroll = (event) => {
    // Hide dropdown when window is scrolled
    if (event.target === document || !event.target.className.includes('CwDropdown-dropDown')) {
      this.setState({
        expanded: false,
        prevExpanded: true
      });
    }
  };

  handleKeyDown = (event) => {
    if (this.keyDownHandlers[event.key])
      this.keyDownHandlers[event.key].call(this, event);
    else {
      return;
    }
  };

  handleInputBlur = () => {
    if (this._ignoreBlur) {
      return;
    }
    // IE fires a blur event when clicking the scrollbar - this prevents the dropdown from closing
    if(this.cwDropdown && this.cwDropdown.isMousedOver()) {
      this.cwButton.focus();
      return;
    }
    this.setState({
      expanded: false,
      highlightedIndex: null
    });
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  highlightItemFromMouse = (index) => {
    this.setState({ highlightedIndex: index });
  };

  handleInputClick = () => {
    if (this.props.disabled) {
      return;
    }
    this.setState((prevState) => {
      return {
        expanded: !prevState.expanded
      };
    }
    );
    this._ignoreBlur = false;
  };

  render() {
    const dropdownSettings = {
      top: this.state.menuTop,
      itemHeight: getMouseMode() ? 24 : 32,
      inputHeight: this.state.inputHeight,
      maxItemsPerPage: 10,
      numOfItems: this.props.items.length,
      numOfExtraItems: 0
    };
    const dropdownPosition = calcDropdownPosition(dropdownSettings);

    let style = { // Pass calculated position coordinates to dropdown with inline style
      left: this.state.menuLeft,
      top: dropdownPosition.top,
      minWidth: this.state.menuWidth,
      height: dropdownPosition.menuHeight
    };

    return (
      <div data-cwid={this.props.cwId}>
        <CwButton
          ref={(menuButton) => this.cwButton = menuButton}
          onBlur={this.handleInputBlur}
          onFocus={this.props.onFocus}
          onKeyDown={this.handleKeyDown}
          onClick={this.handleInputClick}
          tabIndex={this.props.disabled ? -1 : this.props.tabIndex}
          honorTextCasing={this.props.honorTextCasing}
          icon={this.props.icon}
          value={this.props.value}
          rightIcon={this.props.rightIcon}
          isStandard={false}
          disabled={this.props.disabled}
          isMenu={true}
          menuIcon={this.props.menuIcon}
        />
        {this.state.expanded
          ? <CwDropdown
            items={this.props.items}
            value={this.state.value}
            highlightedIndex={this.state.highlightedIndex}
            style={style}
            onClick={(item) => { this.onMenuItemSelected(item); }}
            onMouseEnter={this.highlightItemFromMouse}
            onMouseDown={this.setIgnoreBlur}
            ref={(dropdown) => this.cwDropdown = dropdown}
          />
          : null}
      </div>
    );
  }
}
CwMenuButton.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Array of items to be listed in the dropdown. Must contain at least "id" and "name" fields.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string
    })
  ),
  /**
   * Index of the item selected from the list. Based on "id" field.
   */
  selectedIndex: PropTypes.number,
  /**
   * Callback function that is fired when combobox's selection changes
   */
  onChange: PropTypes.func,
  /**
   * Callback function that is fired when combobox's dropdown is expanded
   */
  onExpand: PropTypes.func,
  /**
   * Used to force combobox's dropdown to be expanded if set to true
   */
  open: PropTypes.bool,
  /**
   * Text for the button
   */
  value: PropTypes.string,
  /**
   * Used to control if we convert button's value to uppercase
   */
  honorTextCasing: PropTypes.bool,
  /**
   * Used to control tab index
   */
  tabIndex: PropTypes.number,
  /**
   * Used for the menu icon
   */
  menuIcon: PropTypes.string,
  /**
   * Optional icon displayed to the left of the button's text if text exist
   */
  icon: PropTypes.string,
  /**
   * Optional icon displayed to the right of the button's text if text exist
   */
  rightIcon: PropTypes.string,
  /**
   *  Callback fired when an item is selected in the drop down
   */
  onDropDownItemClick: PropTypes.func,
  /**
   * Controls if menubutton is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Callback function that is fired when the component is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when the component is blurred
   */
  onBlur: PropTypes.func
};

CwMenuButton.defaultProps = {
  tabIndex: 0,
  items: []
};

export default CwMenuButton;



// WEBPACK FOOTER //
// ./src/components/cwmenubutton/CwMenuButton.jsx